import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type {
    ICustomerArticle,
    ICustomerArticleWithOptions,
    ICustomerCategory,
    ICustomerOffer,
    ICustomerOptionGroup,
} from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";

import { logError } from "@/shared/logging/errors";

import { canBeAddedWithPreselectedOptions } from "@/components/order/optionGroupsValidator";

import { useCartStore } from "./cart";

export const useOfferStore = defineStore("offer", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const cart = useCartStore();

    // define state
    const orderService = ref<IService | null>(null);
    const offer = ref<ICustomerOffer | null>(null);
    const activeCategory = ref<ICustomerCategory | null>(null);
    const isLoading = ref<boolean>(false);

    // define getters
    const itemCategories = computed(() => offer.value?.entries ?? []);
    const hasItemCategories = computed(() => itemCategories.value.length > 0);
    const offerItems = computed(() => getArticlesWithOptions(activeCategory.value?.articles ?? []));
    const isPreorderService = computed(() => orderService.value?.useCase?.usePreorder ?? false);

    // define actions
    async function setOrderService(service: IService | null, reloadOffer: boolean = true) {
        orderService.value = service;

        if (orderService.value == null) {
            setCurrentOffer(null);
            return;
        }

        if (reloadOffer) {
            await loadOffer();
        }
    }

    async function loadOffer() {
        isLoading.value = true;
        try {
            const serviceId = orderService.value!.id;
            const useCase = orderService.value!.useCase;

            const offer = await customerDao.getOffer({
                serviceId: serviceId,
                useCaseId: useCase?.id,
                useCartOffer: isPreorderService.value, // should load offer assigned to cart if it's preorder
            });
            setCurrentOffer(offer);
        } finally {
            isLoading.value = false;
        }
    }

    function setCurrentOffer(currentOffer: ICustomerOffer | null) {
        offer.value = currentOffer;
        cart.setCurrentCart(currentOffer?.cart ?? null);

        if (currentOffer?.entries.length) {
            setActiveCategory(currentOffer?.entries.length ? currentOffer.entries[0] : null);
        } else {
            setActiveCategory(null);
        }
    }

    function setActiveCategory(category: ICustomerCategory | null) {
        activeCategory.value = category;
    }

    function setIsLoading(newIsLoading: boolean) {
        isLoading.value = newIsLoading;
    }

    function getArticlesWithOptions(articles: ICustomerArticle[]) {
        const articlesWithOptions: ICustomerArticleWithOptions[] = [];
        for (const article of articles) {
            const articleWithOptions: ICustomerArticleWithOptions = {
                ...article,
                options: getOptionsForArticle(article),
                canBeAddedWithoutUserSelection: false,
            };
            articleWithOptions.canBeAddedWithoutUserSelection = canBeAddedWithPreselectedOptions(articleWithOptions);

            articlesWithOptions.push(articleWithOptions);
        }
        return articlesWithOptions;
    }

    function getOptionsForArticle(article: ICustomerArticle) {
        const offerOptionGroups = offer.value?.offerOptionGroups ?? [];
        const options: ICustomerOptionGroup[] = [];

        for (const optionGroupId of article.sortedOptionGroups ?? []) {
            const optionGroup = offerOptionGroups.find((optionGroup) => optionGroup.optionGroupId === optionGroupId);
            if (optionGroup == null) {
                logError(`Option group ${optionGroupId} not found for article ${article.articleId}`);
                continue;
            }

            options.push(optionGroup);
        }
        return options;
    }

    return {
        // state
        orderService,
        offer,
        activeCategory,
        isLoading,

        // getters
        itemCategories,
        hasItemCategories,
        offerItems,
        isPreorderService,

        // actions
        setOrderService,
        setActiveCategory,
        loadOffer,
        setCurrentOffer,
        setIsLoading,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useOfferStore, import.meta.hot));
}
